/**
 * Cart Page - Rebuy Integration & AJAX Refresh
 * Handles Rebuy product additions and keeps the cart page in sync
 */

class CartPageController {
  constructor() {
    this.lastRebuyAddAt = 0;
    this.onRebuyAdd = this.onRebuyAdd.bind(this);
    this.onRebuyCartAdd = this.onRebuyCartAdd.bind(this);
    this.onCartUpdated = this.onCartUpdated.bind(this);
    this.init();
  }

  init() {
    // Listen for Rebuy events
    document.addEventListener('rebuy.add', this.onRebuyAdd);
    document.addEventListener('rebuy:cart.add', this.onRebuyCartAdd);

    // Listen for general cart updates
    document.addEventListener('cart:updated', this.onCartUpdated);
    document.addEventListener('ajaxCart:updated', this.onCartUpdated);

    // Listen for Rebuy Smart Cart close (in case items were added via Smart Cart)
    document.addEventListener('rebuy:smartcart.hide', () => this.refreshCartPage());
  }

  onRebuyAdd() {
    this.lastRebuyAddAt = Date.now();
    this.refreshCartPage();
  }

  onRebuyCartAdd() {
    // Debounce: avoid double refresh if rebuy.add fired recently
    if (Date.now() - this.lastRebuyAddAt < 500) return;
    this.refreshCartPage();
  }

  onCartUpdated() {
    // Small delay to ensure Shopify cart is updated
    setTimeout(() => this.refreshCartPage(), 100);
  }

  async refreshCartPage() {
    try {
      const mainCartItems = document.getElementById('main-cart-items');
      const mainCartFooter = document.getElementById('main-cart-footer');
      const cartHeader = document.querySelector('.cart-header');
      const cartItemsWrapper = document.querySelector('.main-cart-wrapper');

      // Determine the section ID from the cart items element
      const sectionId = mainCartItems?.dataset.id;
      if (!sectionId) {
        console.warn('Cart page section ID not found');
        return;
      }

      // Show loading state
      this.showLoading();

      // Fetch updated cart sections
      const [sectionResponse, bubbleResponse, cartDataResponse] = await Promise.all([
        fetch(`${window.routes?.cart_url || '/cart'}?section_id=${sectionId}`),
        fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-icon-bubble`),
        fetch('/cart.js')
      ]);

      const [sectionHtml, bubbleHtml, cartData] = await Promise.all([
        sectionResponse.text(),
        bubbleResponse.text(),
        cartDataResponse.json()
      ]);

      // Parse the HTML
      const parser = new DOMParser();
      const sectionDoc = parser.parseFromString(sectionHtml, 'text/html');
      const bubbleDoc = parser.parseFromString(bubbleHtml, 'text/html');

      // Update cart items
      const newCartItems = sectionDoc.querySelector('.js-contents.cart-items-wrapper');
      const currentCartItems = document.querySelector('.js-contents.cart-items-wrapper');
      if (newCartItems && currentCartItems) {
        currentCartItems.innerHTML = newCartItems.innerHTML;
      }

      // Update cart footer (totals, discount, etc.)
      const newCartFooter = sectionDoc.querySelector('.cart__footer');
      const currentCartFooter = document.querySelector('.cart__footer');
      if (newCartFooter && currentCartFooter) {
        currentCartFooter.innerHTML = newCartFooter.innerHTML;
        // Re-initialize any custom elements in the footer
        this.upgradeCustomElements(currentCartFooter);
      }

      // Update cart header (item count)
      const newCartHeader = sectionDoc.querySelector('.cart-header');
      if (newCartHeader && cartHeader) {
        cartHeader.innerHTML = newCartHeader.innerHTML;
      }

      // Update cart icon bubble
      const newBubble = bubbleDoc.getElementById('cart-icon-bubble');
      const currentBubble = document.getElementById('cart-icon-bubble');
      if (newBubble && currentBubble) {
        currentBubble.innerHTML = newBubble.innerHTML;
      }

      // Handle empty/non-empty cart state
      const cartItems = document.querySelector('cart-items');
      if (cartItems) {
        cartItems.classList.toggle('is-empty', cartData.item_count === 0);
      }
      if (cartItemsWrapper) {
        cartItemsWrapper.classList.toggle('is-cart-empty', cartData.item_count === 0);
      }

      // Refresh dynamic UI elements
      this.refreshDynamicUI();

      // Re-init Rebuy recommendations if needed
      this.reinitRebuy();

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart-page:updated', { detail: { cartData } }));

    } catch (error) {
      console.error('Error refreshing cart page:', error);
    } finally {
      this.hideLoading();
    }
  }

  showLoading() {
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) {
      cartItems.classList.add('cart__items--disabled');
    }
    document.querySelectorAll('.loading__spinner').forEach(spinner => {
      spinner.classList.remove('hidden');
    });
  }

  hideLoading() {
    const cartItems = document.getElementById('main-cart-items');
    if (cartItems) {
      cartItems.classList.remove('cart__items--disabled');
    }
    document.querySelectorAll('.loading__spinner').forEach(spinner => {
      spinner.classList.add('hidden');
    });
  }

  upgradeCustomElements(container) {
    // Upgrade accordion elements
    container.querySelectorAll('accordion-animated').forEach(el => {
      customElements.upgrade(el);
      if (typeof el.refresh === 'function') el.refresh();
    });

    // Upgrade discount code elements
    container.querySelectorAll('cart-discount-code').forEach(el => {
      customElements.upgrade(el);
    });

    // Upgrade cart note elements
    container.querySelectorAll('cart-note').forEach(el => {
      customElements.upgrade(el);
    });
  }

  refreshDynamicUI() {
    // Refresh accordion elements
    document.querySelectorAll('accordion-animated').forEach(item => {
      if (typeof item.refresh === 'function') item.refresh();
    });

    // Update aria-expanded states on summaries
    document.querySelectorAll('.main-cart-footer details summary').forEach(summary => {
      summary.setAttribute('aria-expanded', String(summary.closest('details')?.open));
    });
  }

  reinitRebuy() {
    const container = document.getElementById('rebuy-cart-recommendations');
    if (!container) return;

    // Try to reinitialize Rebuy
    if (window.Rebuy && typeof window.Rebuy.init === 'function') {
      try {
        window.Rebuy.init();
      } catch (e) {
        // Ignore init errors
      }
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartPageController = new CartPageController();
  });
} else {
  window.cartPageController = new CartPageController();
}

// Rebuy initialization with retry (similar to cart drawer)
(function () {
  let rebuyRetryTimer = null;

  function initRebuyWithRetry() {
    const container = document.getElementById('rebuy-cart-recommendations');
    if (!container) return;

    const tryInit = () => {
      if (!window.Rebuy || typeof window.Rebuy.init !== 'function') return false;
      try {
        window.Rebuy.init();
        return true;
      } catch (error) {
        console.error('Rebuy init failed:', error);
        return false;
      }
    };

    if (tryInit()) return;

    let attempts = 0;
    clearTimeout(rebuyRetryTimer);

    const retry = () => {
      attempts += 1;
      if (tryInit() || attempts >= 20) return;
      rebuyRetryTimer = setTimeout(retry, 250);
    };

    rebuyRetryTimer = setTimeout(retry, 250);
  }

  const scheduleRebuyInit = () => setTimeout(initRebuyWithRetry, 50);

  document.addEventListener('DOMContentLoaded', scheduleRebuyInit);
  window.addEventListener('load', scheduleRebuyInit);
  document.addEventListener('cart-page:updated', scheduleRebuyInit);
})();
